// pages/SearchPage.js - Mit Mobile-Optimierungen und Sidebar Button
import { useState, useRef, useEffect, useMemo } from 'react'
import { MAX_MESSAGES } from '../constants'
import { useChat } from '../hooks/useChat'
import { useConversation } from '../hooks/useConversation'
import { useCompany } from '../contexts/CompanyContext'
import { useAuth } from '../contexts/AuthContext'
import ImageHeader from '../components/ImageHeader'
import ChatMessage from '../components/ChatMessage'
import ErrorDisplay from '../components/ErrorDisplay'
import DeviceSelector from '../components/DecviceSelector'
import ConversationSidebar from '../components/ConversationSideBar'
import KnowledgeDetailModal from '../components/KnowledgeDetailModal'
import TutorialPromptModal from '../components/TutorialPromptModal'
import analyticsService from '../services/analyticsService'
import { useAuthState } from '../hooks/useAuthState'
import { useConversationStatus } from '../hooks/useConversationStatus'
import { useUserChatStatus } from '../hooks/useUserChatStatus'
import { useTutorialPrompt } from '../hooks/useTutorialPrompt'
import { getAuth } from 'firebase/auth'
import { getFirestore, doc, getDoc } from 'firebase/firestore'
import { app } from '../firebase'
import UserService from '../services/UserService'
import {
  getDifficultyColor,
  getDifficultyLabel,
  getCategoryIcon,
  getCategoryLabel
} from '../utils/knowledgeHelpers'
import toast from 'react-hot-toast'

import {
  usePageTracking,
  usePerformanceMonitoring,
  useEngagementTracking
} from '../hooks/useAnalytics'
import SendIcon from '@mui/icons-material/Send'
import CircularProgress from '@mui/material/CircularProgress'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import GroupsIcon from '@mui/icons-material/Groups'
import BoltIcon from '@mui/icons-material/Bolt'
import './SearchPage.css'

function SearchPage () {
  const { company, companyId } = useAuthState()
  const { botName, features, headerImageUrl } = useCompany()
  const { user } = useAuth()

  // Analytics Hooks
  usePageTracking()
  usePerformanceMonitoring('SearchPage')
  useEngagementTracking()

  const deduplicateMessages = messages => {
    if (!messages || messages.length === 0) return []
    const seen = new Map()
    return messages.filter(msg => {
      const timestamp =
        msg.timestamp instanceof Date
          ? msg.timestamp.toISOString()
          : msg.timestamp?.toDate?.()?.toISOString() || msg.timestamp || ''
      const contentPreview = msg.content?.substring(0, 100) || ''
      const type = msg.type || 'user'
      const key = `${type}_${timestamp}_${contentPreview}`
      if (seen.has(key)) {
        console.log('Duplikat gefunden und gefiltert:', {
          type,
          contentPreview: contentPreview.substring(0, 30)
        })
        return false
      }
      seen.set(key, true)
      return true
    })
  }

  const [chatState, setChatState] = useState({
    deviceName: '',
    currentMessage: '',
    messages: [],
    isTyping: false,
    conversationId: null,
    error: null,
    performance: null
  })

  const { status, isLoading: statusLoading } = useConversationStatus(
    chatState.conversationId,
    companyId
  )

  const {
    status: userStatus,
    step: userStep,
    progress: userProgress,
    meta: userStatusMeta
  } = useUserChatStatus()

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [selectedKnowledgeItem, setSelectedKnowledgeItem] = useState(null)
  const [currentUser, setCurrentUser] = useState(null)
  const chatContainerRef = useRef(null)
  const inputRef = useRef(null)
  const messageStartTime = useRef(null)
  const sendingLock = useRef(false) // ✅ NEU: Verhindert doppelte Sends

  const {
    conversations,
    currentConversation,
    loading: conversationLoading,
    error: conversationError,
    loadConversation,
    loadConversations,
    addMessage,
    archiveConversation,
    deleteConversation,
    updateConversationTitle,
    clearCurrentConversation,
    clearError: clearConversationError
  } = useConversation()

  const {
    sendMessage,
    resetChat,
    handleKeyPress,
    syncMessagesFromConversation
  } = useChat(chatState, setChatState, null, loadConversations)

  // Tutorial Prompt Hook
  const {
    shouldShowPrompt,
    loading: tutorialPromptLoading,
    dismissPrompt,
    markTutorialCompleted
  } = useTutorialPrompt()

  const [showTutorialPrompt, setShowTutorialPrompt] = useState(false)

  useEffect(() => {
    if (currentConversation) {
      syncMessagesFromConversation(currentConversation)
    } else {
      console.log('📄 No conversation, resetting chat...')
      setChatState(prev => ({
        ...prev,
        messages: [],
        conversationId: null,
        error: null
      }))
    }
  }, [currentConversation?.id, syncMessagesFromConversation])

  const displayMessages = useMemo(() => {
    return deduplicateMessages(chatState.messages)
  }, [chatState.messages])

  // Mobile Detection
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768
      setIsMobile(mobile)
      // Auf Mobile automatisch collapsed
      if (mobile) {
        setSidebarCollapsed(true)
      }
    }
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Tutorial Prompt Modal anzeigen
  useEffect(() => {
    if (shouldShowPrompt && !tutorialPromptLoading) {
      setShowTutorialPrompt(true)
    }
  }, [shouldShowPrompt, tutorialPromptLoading])

  // Modal schließen wenn User eine Conversation erstellt
  useEffect(() => {
    if (conversations.length > 0 && showTutorialPrompt) {
      setShowTutorialPrompt(false)
    }
  }, [conversations.length, showTutorialPrompt])

  // Auto-scroll bei neuen Messages
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [chatState.messages])

  const activeStatus = userStatus || status

  useEffect(() => {
    if (activeStatus === 'completed') {
      setChatState(prev => ({ ...prev, isTyping: false }))
    }
  }, [activeStatus])

  // Auth State
  useEffect(() => {
    const auth = getAuth(app)
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user)
    })
    return () => unsubscribe()
  }, [])

  const statusText = useMemo(() => {
    if (!activeStatus) return null

    // Wenn eine neue Nachricht verarbeitet wird, aber der alte Status
    // noch "completed" ist, zeige einen neutralen Start-Text statt "Fertig!"
    if (activeStatus === 'completed' && chatState.isTyping) {
      return 'Analysiere deine Frage...'
    }

    const map = {
      initializing: 'Initialisiere Chat...',
      analyzing_query: 'Analysiere deine Frage...',
      saving_user_message: 'Speichere Nachricht...',
      fetching_context_from_pinecone: 'Suche in Wissensdatenbank...',
      creating_system_prompt: 'Bereite Kontext vor...',
      generating_response: 'Generiere Antwort...',
      saving_bot_response: 'Speichere Antwort...',
      context_retrieved: 'Kontext gefunden, bereite Antwort vor...',
      completed: 'Fertig!',
      error: 'Es ist ein Fehler aufgetreten.'
    }

    if (typeof activeStatus === 'string' && activeStatus.startsWith('error:')) {
      return activeStatus.substring(6)
    }

    return map[activeStatus] || activeStatus // ← fallback auf status, falls unbekannt
  }, [activeStatus, chatState.isTyping])

  // Langsame Schritte erkennen (für UX-Hinweis)
  const [stepStartTime, setStepStartTime] = useState(null)
  const [slowStep, setSlowStep] = useState(false)

  useEffect(() => {
    if (!activeStatus) {
      setStepStartTime(null)
      setSlowStep(false)
      return
    }
    // Bei Status-Wechsel Startzeit neu setzen
    setStepStartTime(Date.now())
    setSlowStep(false)
  }, [activeStatus])

  useEffect(() => {
    if (!stepStartTime || !activeStatus) return

    const timeout = setTimeout(() => {
      // Wenn nach 8000ms immer noch derselbe Status aktiv ist, Hinweis anzeigen
      setSlowStep(true)
    }, 8000)

    return () => clearTimeout(timeout)
  }, [stepStartTime, activeStatus])

  const handleDeviceChange = deviceName => {
    setChatState(prev => ({ ...prev, deviceName }))
    analyticsService.trackUserAction('device_selected', {
      device_name: deviceName,
      has_conversation: !!chatState.conversationId
    })

    // Wenn noch keine Nachrichten existieren (Startzustand),
    // nach der Gerätewahl direkt das Eingabefeld fokussieren
    if (!chatState.messages || chatState.messages.length === 0) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
        }
      }, 0)
    }
  }

  // Feedback Handler
  const handleFeedback = async ({
    conversationId,
    messageIndex,
    feedbackType,
    message
  }) => {
    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions')
      const { app } = await import('../firebase')

      const functions = getFunctions(app)
      const submitFeedback = httpsCallable(functions, 'submitChatFeedback')

      await submitFeedback({
        conversationId,
        messageIndex,
        feedbackType,
        message
      })

      console.log('✅ Feedback submitted:', { feedbackType, messageIndex })

      // Optional: Analytics tracken
      analyticsService.trackUserAction('chat_feedback_submitted', {
        feedback_type: feedbackType,
        conversation_id: conversationId,
        message_index: messageIndex
      })
    } catch (error) {
      console.error('❌ Feedback error:', error)
      analyticsService.trackError('chat_feedback_error', {
        message: error.message,
        severity: 'low'
      })
      throw error
    }
  }

  // Knowledge Modal Handlers
  const loadKnowledgeItem = async knowledgeId => {
    try {
      if (!currentUser) {
        console.warn('User not authenticated')
        toast.error('Bitte melden Sie sich an, um Details anzuzeigen')
        return
      }

      const db = getFirestore(app)
      const userCompanyId = await UserService.getUserCompanyId(currentUser.uid)

      const docRef = doc(db, 'companies', userCompanyId, 'knowledge', knowledgeId)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists() && docSnap.data().status === 'approved') {
        setSelectedKnowledgeItem({
          id: docSnap.id,
          ...docSnap.data()
        })
      } else {
        toast.error('Wissen nicht gefunden oder noch nicht freigegeben')
      }
    } catch (error) {
      console.error('Error loading knowledge item:', error)
      toast.error('Fehler beim Laden des Wissens')
    }
  }

  const handleKnowledgeClick = knowledgeId => {
    if (!knowledgeId) {
      console.error('No knowledgeId provided')
      return
    }
    loadKnowledgeItem(knowledgeId)
  }

  const handleCloseModal = () => {
    setSelectedKnowledgeItem(null)
  }

  const handleSendMessage = async () => {
    // ✅ Lock-Check: Verhindert doppelte Sends (besonders wichtig auf Mobile!)
    if (sendingLock.current) {
      console.log('⚠️ Send bereits in Progress - ignoriert')
      return
    }

    const { currentMessage, deviceName, messages } = chatState

    // Vor neuer Anfrage: lokalen Slow-Step-Hinweis zurücksetzen,
    // damit kein alter Hinweis für einen Moment sichtbar ist
    setSlowStep(false)
    setStepStartTime(null)
    if (!currentMessage.trim() || !deviceName.trim()) {
      const errorMsg = 'Bitte eine Nachricht UND einen Gerätenamen eingeben.'
      setChatState(prev => ({ ...prev, error: errorMsg }))
      analyticsService.trackError('validation_error', {
        error_type: 'missing_input',
        has_device: !!deviceName.trim(),
        has_message: !!currentMessage.trim(),
        severity: 'low'
      })
      return
    }

    // Duplikat-Prävention: Prüfe ob gleiche Message in letzten 2 Sekunden
    const now = new Date()
    const recentUserMessage = messages
      .filter(m => m.type === 'user')
      .slice(-1)[0]
    if (
      recentUserMessage &&
      recentUserMessage.content === currentMessage.trim() &&
      now - new Date(recentUserMessage.timestamp) < 2000
    ) {
      console.log('⚠️ Duplikat-Message verhindert (zu schnell)')
      analyticsService.trackUserAction('duplicate_message_prevented', {
        time_diff_ms: now - new Date(recentUserMessage.timestamp)
      })
      return
    }

    // ✅ Lock setzen - verhindert doppelte Clicks/Touches
    sendingLock.current = true

    messageStartTime.current = Date.now()
    analyticsService.trackSearch(currentMessage, deviceName, null)

    // ✅ WICHTIG: Nicht hier die userMessage erstellen!
    // useChat.sendMessage() macht das bereits!
    // Nur currentMessage leeren und isTyping setzen
    setChatState(prev => ({
      ...prev,
      currentMessage: '',
      isTyping: true,
      error: null
    }))

    try {
      await sendMessage()
      const responseTime = Date.now() - messageStartTime.current
      const lastMessage = chatState.messages[chatState.messages.length - 1]
      analyticsService.trackChatMessage(
        'bot',
        lastMessage?.content?.length || 0,
        {
          deviceName,
          responseTime,
          sourcesCount: lastMessage?.sources?.length || 0,
          hasErrorCode: /[EF]-?\d{1,5}/.test(currentMessage)
        }
      )
      if (chatState.performance) {
        analyticsService.trackChatPerformance(chatState.performance)
      }
      analyticsService.trackConversion('successful_query', 1)
    } catch (error) {
      console.error('Send message error:', error)
      analyticsService.trackError('chat_send_error', {
        message: error.message,
        stack: error.stack?.substring(0, 500),
        device_name: deviceName,
        query_length: currentMessage.length,
        severity: 'high'
      })
      setChatState(prev => ({
        ...prev,
        error: 'Nachricht konnte nicht gesendet werden.',
        isTyping: false
      }))
    } finally {
      // ✅ Lock immer freigeben (auch bei Fehler)
      sendingLock.current = false
    }
  }

  const handleResetChat = () => {
    analyticsService.trackUserAction('chat_reset', {
      message_count: chatState.messages.length,
      device_name: chatState.deviceName
    })
    resetChat()
    clearCurrentConversation()
  }

  const handleLoadConversation = async conversationId => {
    try {
      console.log('📂 Loading conversation:', conversationId)
      const conversation = await loadConversation(conversationId)

      if (conversation) {
        console.log(
          '✅ Conversation loaded with',
          conversation.messages?.length,
          'messages'
        )
      }

      // Auf Mobile: Sidebar nach Laden schließen
      if (isMobile) {
        setSidebarCollapsed(true)
      }
    } catch (error) {
      console.error('❌ Error loading conversation:', error)
      setChatState(prev => ({
        ...prev,
        error: 'Fehler beim Laden der Conversation'
      }))
    }
  }

  const handleArchiveConversation = async conversationId => {
    try {
      await archiveConversation(conversationId)
      analyticsService.trackUserAction('conversation_archived', {
        conversation_id: conversationId
      })
      if (chatState.conversationId === conversationId) {
        handleResetChat()
      }
    } catch (error) {
      analyticsService.trackError('conversation_archive_error', {
        conversation_id: conversationId,
        message: error.message,
        severity: 'medium'
      })
      setChatState(prev => ({
        ...prev,
        error: 'Fehler beim Archivieren: ' + error.message
      }))
    }
  }

  const handleDeleteConversation = async conversationId => {
    try {
      await deleteConversation(conversationId)
      analyticsService.trackUserAction('conversation_deleted', {
        conversation_id: conversationId
      })
    } catch (error) {
      analyticsService.trackError('conversation_delete_error', {
        conversation_id: conversationId,
        message: error.message,
        severity: 'medium'
      })
    }
  }

  const handleUpdateTitle = async (conversationId, newTitle) => {
    try {
      await updateConversationTitle(conversationId, newTitle)
      analyticsService.trackUserAction('conversation_title_updated', {
        conversation_id: conversationId,
        title_length: newTitle.length
      })
    } catch (error) {
      analyticsService.trackError('conversation_title_update_error', {
        conversation_id: conversationId,
        message: error.message,
        severity: 'low'
      })
    }
  }

  const handleToggleSidebar = () => {
    const newState = !sidebarCollapsed
    setSidebarCollapsed(newState)
    analyticsService.trackUserAction('sidebar_toggled', {
      new_state: newState ? 'collapsed' : 'expanded',
      is_mobile: isMobile
    })
  }

  const handleInputFocus = () => {
    analyticsService.trackUserAction('chat_input_focused')
  }

  const handleInputChange = e => {
    const newValue = e.target.value
    setChatState(prev => ({ ...prev, currentMessage: newValue }))
    if (newValue.length === 1) {
      analyticsService.trackUserAction('started_typing')
    }
  }

  return (
    <div className='search-page-layout'>
      {/* NEUER MOBILE SIDEBAR BUTTON - außerhalb der Sidebar! */}
      {isMobile && sidebarCollapsed && (
        <button
          className='mobile-sidebar-toggle-btn'
          onClick={handleToggleSidebar}
          aria-label='Conversations anzeigen'
        >
          <svg
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
          >
            <line x1='3' y1='6' x2='21' y2='6' />
            <line x1='3' y1='12' x2='21' y2='12' />
            <line x1='3' y1='18' x2='21' y2='18' />
          </svg>
          {conversations.length > 0 && (
            <span className='conversation-count-badge'>
              {conversations.length}
            </span>
          )}
        </button>
      )}

      {/* Mobile Overlay */}
      {isMobile && !sidebarCollapsed && (
        <div
          className='mobile-sidebar-overlay active'
          onClick={handleToggleSidebar}
        />
      )}

      {/* Permanente Sidebar */}
      <ConversationSidebar
        isPermanent={true}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        conversations={conversations}
        currentConversation={currentConversation}
        loading={conversationLoading}
        onLoadConversation={handleLoadConversation}
        onArchiveConversation={handleArchiveConversation}
        onDeleteConversation={handleDeleteConversation}
        onUpdateTitle={handleUpdateTitle}
        onNewChat={handleResetChat}
      />

      {/* Main Chat Area */}
      <div
        className={`chat-main-area ${
          sidebarCollapsed ? 'sidebar-collapsed' : ''
        }`}
      >
        <ErrorDisplay
          error={chatState.error || conversationError}
          onDismiss={() => {
            setChatState(prev => ({ ...prev, error: null }))
            clearConversationError()
          }}
        />

        <div ref={chatContainerRef} className='chat-container'>
          {features?.imageHeader && (
            <ImageHeader
              imageUrl={headerImageUrl}
              companyId={user?.userData?.companyId}
            />
          )}
          {displayMessages.length === 0 && (
            <div className='welcome-message'>
              <h2>{company ? <span>{company} </span> : ''}{botName}</h2>
              <p>Wählen Sie Ihr Gerät und stellen Sie Ihre Frage</p>

              <div className='quick-start-selector'>
                <DeviceSelector
                  value={chatState.deviceName}
                  onChange={handleDeviceChange}
                  disabled={chatState.isTyping}
                  showLabel={false}
                  compact={false}
                />
              </div>
            </div>
          )}

          {displayMessages.map((message, index) => (
            <ChatMessage
              key={message.id || index}
              message={message}
              conversationId={chatState.conversationId}
              messageIndex={index}
              onFeedback={handleFeedback}
              onKnowledgeClick={handleKnowledgeClick}
            />
          ))}

          {chatState.isTyping && (
            <div className='typing-indicator'>
              <div className='spinner'></div>
              <span>{statusText || 'Lade...'}</span>
              {typeof userProgress === 'number' && userProgress > 0 && (
                <div className='typing-progress-bar'>
                  <div
                    className='typing-progress-fill'
                    style={{ width: `${Math.min(userProgress, 100)}%` }}
                  />
                </div>
              )}
              {slowStep && activeStatus !== 'completed' && (
                <div className='typing-hint'>
                  Die Suche dauert länger als üblich. Bitte einen Moment
                  Geduld, die Manuals sind sehr umfangreich.
                </div>
              )}
            </div>
          )}
        </div>

        <div className='chat-input-area'>
          {/* {displayMessages.length > 0 && !currentConversation && (
            <div className='device-selector-compact'>
              <DeviceSelector
                value={chatState.deviceName}
                onChange={handleDeviceChange}
                disabled={chatState.isTyping}
                showLabel={true}
                compact={false}
              />
            </div>
          )} */}

          <div className='input-wrapper'>
            <textarea
              ref={inputRef}
              value={chatState.currentMessage}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyPress={handleKeyPress}
              placeholder={
                chatState.deviceName
                  ? 'Ihre Nachricht...'
                  : 'Bitte wählen Sie zuerst ein Gerät'
              }
              disabled={!chatState.deviceName.trim() || chatState.isTyping}
              rows={1}
            />
            <button
              className='send-btn'
              onClick={handleSendMessage}
              disabled={
                chatState.isTyping ||
                !chatState.currentMessage.trim() ||
                !chatState.deviceName.trim()
              }
            >
              {chatState.isTyping ? (
                <CircularProgress size={24} sx={{ color: 'white' }} />
              ) : (
                <SendIcon sx={{ fontSize: 24 }} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Knowledge Detail Modal */}
      {selectedKnowledgeItem && (
        <KnowledgeDetailModal
          item={selectedKnowledgeItem}
          onClose={handleCloseModal}
          currentUser={currentUser}
          userRole={company?.role}
          getCategoryIcon={getCategoryIcon}
          getCategoryLabel={getCategoryLabel}
          getDifficultyColor={getDifficultyColor}
          getDifficultyLabel={getDifficultyLabel}
        />
      )}

      {/* Tutorial Prompt Modal */}
      <TutorialPromptModal
        isOpen={showTutorialPrompt}
        onClose={() => setShowTutorialPrompt(false)}
        onDismiss={dismissPrompt}
      />
    </div>
  )
}

export default SearchPage
